import { supabase } from '../lib/supabase';
import ProgressMonitorService from './ProgressMonitorService';

/**
 * RecoveryEngineService
 *
 * Generates recovery options when student falls behind:
 * - COMPRESS_SPRINT: Skip optional tasks in next sprint
 * - SKIP_OPTIONAL: Skip optional sprints (e.g., optional essays)
 * - EXTEND_DAY: Add evening sprint to catch up
 * - SHIFT_DEADLINE: Move schools to Regular Decision
 * - EMERGENCY_TRIAGE: Keep only top choice + safety
 *
 * Called when:
 * - Student falls 1+ sprints behind
 * - Milestone is at risk
 * - Deadline approaching with incomplete work
 */
class RecoveryEngineService {
  /**
   * Generate a recovery plan for a student
   * @param {string} userId - Student ID
   * @param {string} triggerAlertId - Optional alert that triggered recovery
   * @returns {Promise<Object>} Recovery plan with options
   */
  async generateRecoveryPlan(userId, triggerAlertId = null) {
    try {
      console.log('[RecoveryEngineService] Generating recovery plan for user:', userId);

      // Get current progress
      const snapshot = await ProgressMonitorService.getCurrentProgress(userId);
      console.log('[RecoveryEngineService] Progress snapshot:', snapshot);

      if (!snapshot) {
        throw new Error('No progress snapshot available. Please start a sprint first to generate progress data.');
      }

      // Analyze the situation
      const analysis = await this.analyzeSituation(snapshot);
      console.log('[RecoveryEngineService] Situation analysis:', analysis);

      // Generate recovery options
      const options = await this.generateOptions(snapshot, analysis);
      console.log('[RecoveryEngineService] Generated options:', options.length);

      if (options.length === 0) {
        console.warn('[RecoveryEngineService] No recovery options could be generated. Analysis:', {
          can_compress: analysis.can_compress,
          can_skip_optional: analysis.can_skip_optional,
          can_extend_day: analysis.can_extend_day,
          sprints_behind: analysis.sprints_behind,
          optional_sprints_count: analysis.optional_sprints_count,
          is_emergency: analysis.is_emergency
        });
      }

      // Rank options by feasibility
      const rankedOptions = this.rankOptions(options, analysis);

      // Create recovery plan
      const { data: plan, error } = await supabase
        .from('recovery_plans')
        .insert({
          user_id: userId,
          schedule_id: snapshot.schedule_id,
          trigger_alert_id: triggerAlertId,
          situation_analysis: analysis,
          recommended_option_id: rankedOptions[0]?.id || null,
          status: 'PENDING',
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      console.log('[RecoveryEngineService] Recovery plan created:', plan.id);

      // Save recovery options
      for (const option of rankedOptions) {
        const { data: savedOption, error: optionError } = await supabase
          .from('recovery_options')
          .insert({
            recovery_plan_id: plan.id,
            ...option,
            created_at: new Date().toISOString()
          })
          .select()
          .single();

        if (optionError) {
          console.error('[RecoveryEngineService] Error saving option:', optionError);
          throw optionError;
        }
        console.log('[RecoveryEngineService] Saved recovery option:', savedOption.type);
      }

      // Fetch complete plan with options
      const { data: completePlan, error: fetchError } = await supabase
        .from('recovery_plans')
        .select('*, recovery_options(*)')
        .eq('id', plan.id)
        .single();

      if (fetchError) throw fetchError;

      console.log('[RecoveryEngineService] Complete plan fetched with', completePlan.recovery_options?.length || 0, 'options');
      return completePlan;
    } catch (error) {
      console.error('[RecoveryEngineService] Error generating recovery plan:', error);
      throw error;
    }
  }

  /**
   * Analyze the current situation
   * @param {Object} snapshot - Progress snapshot
   * @returns {Object} Situation analysis
   */
  async analyzeSituation(snapshot) {
    // Get schedule and sprints
    const { data: schedule, error: scheduleError } = await supabase
      .from('sprint_schedules')
      .select('*')
      .eq('id', snapshot.schedule_id)
      .single();

    if (scheduleError) throw scheduleError;

    const { data: sprints, error: sprintsError } = await supabase
      .from('sprints')
      .select('*')
      .eq('schedule_id', snapshot.schedule_id)
      .order('sprint_number', { ascending: true });

    if (sprintsError) throw sprintsError;

    const now = new Date();
    const endDate = new Date(schedule.end_date);
    const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    const hoursRemaining = (endDate - now) / (1000 * 60 * 60);

    // Count optional sprints
    const optionalSprints = sprints.filter(s =>
      s.is_optional && s.status !== 'COMPLETE' && s.status !== 'SKIPPED'
    );

    // Count sprints by type
    const sprintsByType = sprints.reduce((acc, sprint) => {
      if (sprint.status !== 'COMPLETE' && sprint.status !== 'SKIPPED') {
        acc[sprint.type] = (acc[sprint.type] || 0) + 1;
      }
      return acc;
    }, {});

    return {
      risk_level: snapshot.risk_level,
      sprints_behind: snapshot.sprints_behind,
      minutes_behind: snapshot.minutes_behind,
      days_remaining: daysRemaining,
      hours_remaining: hoursRemaining,
      incomplete_sprints: snapshot.sprints_total - snapshot.sprints_completed,
      optional_sprints_count: optionalSprints.length,
      optional_sprint_ids: optionalSprints.map(s => s.id),
      sprints_by_type: sprintsByType,
      time_remaining: snapshot.time_remaining,
      can_compress: optionalSprints.length > 0,
      can_skip_optional: optionalSprints.length > 0,
      can_extend_day: daysRemaining > 0,
      is_emergency: snapshot.risk_level === 'EMERGENCY' || hoursRemaining < 24
    };
  }

  /**
   * Generate recovery options based on situation
   * @param {Object} snapshot - Progress snapshot
   * @param {Object} analysis - Situation analysis
   * @returns {Promise<Array>} Array of recovery options
   */
  async generateOptions(snapshot, analysis) {
    const options = [];

    // Option 1: Compress Sprint (skip optional tasks)
    if (analysis.can_compress && analysis.sprints_behind <= 2) {
      options.push(await this.generateCompressOption(snapshot, analysis));
    }

    // Option 2: Skip Optional Sprints
    if (analysis.can_skip_optional && analysis.optional_sprints_count > 0) {
      options.push(await this.generateSkipOptionalOption(snapshot, analysis));
    }

    // Option 3: Extend Day (add evening sprint)
    if (analysis.can_extend_day && analysis.sprints_behind === 1) {
      options.push(await this.generateExtendDayOption(snapshot, analysis));
    }

    // Option 4: Shift Deadline (move schools to RD)
    if (analysis.sprints_behind >= 2) {
      options.push(await this.generateShiftDeadlineOption(snapshot, analysis));
    }

    // Option 5: Emergency Triage (only top choice + safety)
    if (analysis.is_emergency || analysis.sprints_behind >= 3) {
      options.push(await this.generateEmergencyTriageOption(snapshot, analysis));
    }

    return options.filter(Boolean);
  }

  /**
   * Generate compress sprint option
   */
  async generateCompressOption(snapshot, analysis) {
    const timeSaved = Math.min(30, analysis.minutes_behind); // Estimate 30 min saved

    return {
      type: 'COMPRESS_SPRINT',
      title: 'Compress Next Sprint',
      description: 'Skip optional tasks in your next sprint to catch up. Focus on essential work only.',
      time_saved: timeSaved,
      quality_impact: -0.1, // Minimal quality impact
      effort_required: 'LOW',
      tradeoffs: [
        'Some nice-to-have tasks will be skipped',
        'Essay may be slightly less polished',
        'Research may be less comprehensive'
      ],
      feasibility: 0.9,
      changes: {
        action: 'compress_sprint',
        sprints_affected: 1,
        estimated_time_saved: timeSaved
      }
    };
  }

  /**
   * Generate skip optional option
   */
  async generateSkipOptionalOption(snapshot, analysis) {
    const timeSaved = analysis.optional_sprints_count * 60; // Estimate 60 min per optional sprint

    return {
      type: 'SKIP_OPTIONAL',
      title: 'Skip Optional Essays',
      description: `Skip ${analysis.optional_sprints_count} optional sprint(s) to focus on required work.`,
      time_saved: timeSaved,
      quality_impact: -0.2, // Some quality impact
      effort_required: 'NONE',
      tradeoffs: [
        'Optional essays won\'t be submitted',
        'May reduce chances at competitive schools',
        'Can still add later if time allows'
      ],
      feasibility: 0.85,
      changes: {
        action: 'skip_optional',
        sprint_ids: analysis.optional_sprint_ids,
        sprints_count: analysis.optional_sprints_count,
        estimated_time_saved: timeSaved
      }
    };
  }

  /**
   * Generate extend day option
   */
  async generateExtendDayOption(snapshot, analysis) {
    return {
      type: 'EXTEND_DAY',
      title: 'Add Evening Sprint',
      description: 'Add a 60-90 minute evening work session today to catch up.',
      time_saved: 0, // Doesn't save time, adds time
      quality_impact: 0, // No quality impact
      effort_required: 'MEDIUM',
      tradeoffs: [
        'Less rest/downtime today',
        'May impact energy tomorrow',
        'Keeps all original work items'
      ],
      feasibility: 0.7,
      changes: {
        action: 'add_evening_sprint',
        estimated_duration: 90,
        suggested_time: '19:00'
      }
    };
  }

  /**
   * Generate shift deadline option
   */
  async generateShiftDeadlineOption(snapshot, analysis) {
    // Get sprints by school to estimate impact
    const { data: sprints } = await supabase
      .from('sprints')
      .select('school_id, estimated_duration')
      .eq('schedule_id', snapshot.schedule_id)
      .not('school_id', 'is', null)
      .in('status', ['NOT_STARTED', 'IN_PROGRESS']);

    const schoolGroups = sprints.reduce((acc, sprint) => {
      if (!acc[sprint.school_id]) {
        acc[sprint.school_id] = {
          school_id: sprint.school_id,
          sprints: 0,
          time: 0
        };
      }
      acc[sprint.school_id].sprints++;
      acc[sprint.school_id].time += sprint.estimated_duration;
      return acc;
    }, {});

    const timeSaved = Math.max(...Object.values(schoolGroups).map(g => g.time));

    return {
      type: 'SHIFT_DEADLINE',
      title: 'Move Schools to Regular Decision',
      description: 'Shift 1-2 schools from Early to Regular Decision deadlines to focus on top priorities.',
      time_saved: timeSaved,
      quality_impact: 0.1, // Actually improves quality on remaining schools
      effort_required: 'LOW',
      tradeoffs: [
        'Less acceptance advantage for shifted schools',
        'More time to perfect remaining applications',
        'Can still apply RD with stronger materials'
      ],
      feasibility: 0.75,
      changes: {
        action: 'shift_deadline',
        school_options: Object.keys(schoolGroups),
        estimated_time_saved: timeSaved
      }
    };
  }

  /**
   * Generate emergency triage option
   */
  async generateEmergencyTriageOption(snapshot, analysis) {
    return {
      type: 'EMERGENCY_TRIAGE',
      title: 'Emergency Triage: Top Choice + Safety Only',
      description: 'Focus solely on your #1 choice school and one safety school. Defer all others to RD.',
      time_saved: analysis.time_remaining * 0.6, // Save 60% of remaining time
      quality_impact: 0.3, // Significant quality improvement on remaining schools
      effort_required: 'NONE',
      tradeoffs: [
        'Only 2 schools in Early round',
        'Significantly reduces immediate workload',
        'Maximizes quality on top priority',
        'Other schools can be added RD'
      ],
      feasibility: 0.9,
      changes: {
        action: 'emergency_triage',
        schools_to_keep: 2,
        estimated_time_saved: Math.floor(analysis.time_remaining * 0.6)
      }
    };
  }

  /**
   * Rank options by feasibility and effectiveness
   * @param {Array} options - Array of recovery options
   * @param {Object} analysis - Situation analysis
   * @returns {Array} Ranked options
   */
  rankOptions(options, analysis) {
    // Score each option based on:
    // - Feasibility
    // - Time saved
    // - Quality impact (prefer minimal impact)
    // - Effort required (prefer low effort)

    const scored = options.map(option => {
      let score = 0;

      // Feasibility (40% weight)
      score += option.feasibility * 40;

      // Time saved relative to need (30% weight)
      const timeSavedScore = Math.min(option.time_saved / analysis.minutes_behind, 1);
      score += timeSavedScore * 30;

      // Quality impact (20% weight) - prefer minimal negative impact
      const qualityScore = 1 - Math.abs(option.quality_impact);
      score += qualityScore * 20;

      // Effort required (10% weight)
      const effortScores = { NONE: 1, LOW: 0.7, MEDIUM: 0.5, HIGH: 0.3 };
      score += (effortScores[option.effort_required] || 0.5) * 10;

      return {
        ...option,
        score
      };
    });

    // Sort by score descending
    scored.sort((a, b) => b.score - a.score);

    // Add rank and mark recommended
    return scored.map((option, index) => ({
      ...option,
      rank: index + 1,
      is_recommended: index === 0
    }));
  }

  /**
   * Get recovery plans for a student
   * @param {string} userId - Student ID
   * @param {string} status - Optional status filter
   * @returns {Promise<Array>} Array of recovery plans
   */
  async getRecoveryPlans(userId, status = null) {
    try {
      let query = supabase
        .from('recovery_plans')
        .select('*, recovery_options(*)')
        .eq('user_id', userId);

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('[RecoveryEngineService] Error getting recovery plans:', error);
      throw error;
    }
  }

  /**
   * Get a specific recovery plan
   * @param {string} planId - Recovery plan ID
   * @returns {Promise<Object>} Recovery plan with options
   */
  async getRecoveryPlan(planId) {
    try {
      const { data, error } = await supabase
        .from('recovery_plans')
        .select('*, recovery_options(*)')
        .eq('id', planId)
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('[RecoveryEngineService] Error getting recovery plan:', error);
      throw error;
    }
  }
}

export default new RecoveryEngineService();
